import { Grid, useTheme } from '@mui/material';
import React, { useCallback } from 'react';
import { BooleanParam, useQueryParam } from 'use-query-params';

import { useTableEditingContext } from 'components/atoms/EditableDataGrid/TableEditingContext';
import {
  editDrawerBottomPadding,
  Main,
  overviewDrawerWidth,
  useStudyDashboardStyles,
} from 'components/atoms/FlexContainers/FlexContainers';
import { Permission } from 'interfaces/permissionOption';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';
import useStudy from 'utils/useStudy';
import ProceduresPage from './ProceduresPage';
import { useCasesViewMode } from './ProceduresPage/useCasesViewMode';
import { proceduresContentHorizontalPaddingX, proceduresContentTopPadding } from './studyDashboardSpacing';
import UploadSlidesModal from './UploadSlidesModal/UploadSlidesModal';

interface Props {
  onDatasetScroll?: (scrollTop: number) => void;
}

const MainContent = ({ onDatasetScroll }: Props) => {
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const { bulkEditMode } = useTableEditingContext();

  const { queryParams } = useEncodedFilters();
  const { study: currentStudy } = useStudy(queryParams.filters?.studyId, { enabled: true, withStatistics: true });

  const [isUploadSlidesModalOpen, setIsUploadSlidesModalOpen] = useQueryParam('isUploadSlidesModalOpen', BooleanParam);

  const displayProcedureModeToggle = hasPermission(Permission.ViewCasesTable);

  const [advancedSearchDrawerOpen] = useQueryParam('advancedSearchDrawerOpen', BooleanParam);
  const { casesViewMode } = useCasesViewMode();
  const procedureViewMode = displayProcedureModeToggle ? casesViewMode : 'card';
  const shouldUseInnerScroll = procedureViewMode === 'table';
  const contentBottomPadding = procedureViewMode === 'table' ? theme.spacing(3) : editDrawerBottomPadding;

  const onUploadSlidesModalClose = () => {
    setIsUploadSlidesModalOpen(false);
  };

  const handleDatasetScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      if (shouldUseInnerScroll) {
        onDatasetScroll?.(event.currentTarget.scrollTop);
      }
    },
    [onDatasetScroll, shouldUseInnerScroll]
  );

  const { mainMarginBottom } = useStudyDashboardStyles();

  return (
    <Main
      leftDrawerOpen={advancedSearchDrawerOpen}
      rightDrawerOpen={bulkEditMode && procedureViewMode === 'card'}
      rightDrawerWidth={overviewDrawerWidth}
      paddingBottom={contentBottomPadding}
      marginBottom={shouldUseInnerScroll ? mainMarginBottom : 0}
      useOwnScroll={shouldUseInnerScroll}
      onScroll={handleDatasetScroll}
    >
      <Grid
        item
        container
        sx={{
          display: 'block',
          height: shouldUseInnerScroll ? '100%' : 'auto',
          pt: proceduresContentTopPadding,
          px: proceduresContentHorizontalPaddingX,
          overflow: shouldUseInnerScroll ? 'hidden' : 'visible',
        }}
      >
        <UploadSlidesModal
          isOpen={isUploadSlidesModalOpen}
          studyId={queryParams.filters?.studyId}
          onClose={onUploadSlidesModalClose}
        />
        <ProceduresPage displayProcedureModeToggle={displayProcedureModeToggle} study={currentStudy} />
      </Grid>
    </Main>
  );
};

export default MainContent;
